/**
 * Manages per-row behaviour for collapsible traveller cards:
 * - DOB → TravellerType auto-default (respects user override)
 * - Summary line refresh when the form collapses
 * - Type icon swap
 *
 * Call setupTravellerRow(card) once per card, including the owner card.
 * Call it for new cards immediately after they are appended to the DOM.
 */

type TravellerType = 'Adult' | 'Child' | 'Baby';

const ICON_MAP: Record<TravellerType, string> = {
    Adult: 'fas fa-user',
    Child: 'fas fa-child',
    Baby:  'fas fa-baby',
};

function typeFromDob(dob: string): TravellerType {
    if (!dob) return 'Adult';
    const birth = new Date(dob);
    const now   = new Date();
    const years = now.getFullYear() - birth.getFullYear()
        - (now < new Date(now.getFullYear(), birth.getMonth(), birth.getDate()) ? 1 : 0);
    if (years < 3)  return 'Baby';
    if (years < 13) return 'Child';
    return 'Adult';
}

function refreshSummary(card: HTMLElement): void {
    const firstName = (card.querySelector<HTMLInputElement>('[data-field="FirstName"]')?.value || '').trim();
    const lastName  = (card.querySelector<HTMLInputElement>('[data-field="LastName"]')?.value  || '').trim();
    const dob       = (card.querySelector<HTMLInputElement>('[data-field="DateOfBirth"]')?.value || '').trim();
    const email     = (card.querySelector<HTMLInputElement>('[data-field="Email"]')?.value       || '').trim();
    const typeSelect = card.querySelector<HTMLSelectElement>('.traveller-type-select');
    const type      = (typeSelect?.value as TravellerType) || 'Adult';

    const nameEl  = card.querySelector<HTMLElement>('.traveller-summary-name');
    const dobEl   = card.querySelector<HTMLElement>('.traveller-summary-dob');
    const emailEl = card.querySelector<HTMLElement>('.traveller-summary-email');
    const iconEl  = card.querySelector<HTMLElement>('.traveller-type-icon');

    if (nameEl) {
        const full = [firstName, lastName].filter(Boolean).join(' ');
        nameEl.textContent    = full || nameEl.dataset.placeholder || '';
        nameEl.classList.toggle('text-muted',    !full);
        nameEl.classList.toggle('fst-italic',    !full);
    }
    if (dobEl)   dobEl.textContent   = dob   ? `· ${dob}`   : '';
    if (emailEl) emailEl.textContent = email ? `· ${email}` : '';
    if (iconEl) {
        const i = iconEl.querySelector('i');
        if (i) i.className = `${ICON_MAP[type]} text-muted`;
    }
}

export function setupTravellerRow(card: HTMLElement): void {
    const dobInput   = card.querySelector<HTMLInputElement>('.traveller-dob');
    const typeSelect = card.querySelector<HTMLSelectElement>('.traveller-type-select');
    const collapseEl = card.querySelector<HTMLElement>('.traveller-form-body');
    const saveBtn    = card.querySelector<HTMLButtonElement>('.traveller-save-btn');
    const nameEl     = card.querySelector<HTMLElement>('.traveller-summary-name');

    // Store placeholder text for empty state
    if (nameEl && !nameEl.dataset.placeholder) {
        nameEl.dataset.placeholder = nameEl.textContent || '';
    }

    // DOB → Type auto-default
    if (dobInput && typeSelect) {
        dobInput.addEventListener('change', () => {
            if (typeSelect.dataset.typeUserSet !== 'true') {
                typeSelect.value = typeFromDob(dobInput.value);
            }
            refreshSummary(card);
        });
    }

    // User manually changed type — mark as overridden
    if (typeSelect) {
        typeSelect.addEventListener('change', () => {
            typeSelect.dataset.typeUserSet = 'true';
            refreshSummary(card);
        });
    }

    // Refresh summary whenever the form body collapses (via Edit toggle or Save button)
    if (collapseEl) {
        collapseEl.addEventListener('hide.bs.collapse', () => refreshSummary(card));
    }

    // Save button also refreshes immediately (covers the instant before collapse fires)
    if (saveBtn) {
        saveBtn.addEventListener('click', () => refreshSummary(card));
    }

    // Refresh immediately in case values are pre-filled (edit page re-use or autofill)
    refreshSummary(card);
}
